// Re-export Ant Design components for easy importing
import React from 'react';
import { Button as AntButton, ButtonProps as AntButtonProps } from 'antd';

// Custom Button wrapper to maintain compatibility with existing props
interface CustomButtonProps extends Omit<AntButtonProps, 'type' | 'size' | 'variant' | 'iconPosition'> {
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'destructive' | 'outline' | 'outlined' | 'secondary' | 'ghost' | 'link';
  size?: 'xs' | 'sm' | 'default' | 'lg' | 'xl' | 'icon';
  iconName?: string;
  iconPosition?: 'left' | 'right' | 'start' | 'end';
  fullWidth?: boolean;
  loading?: boolean;
}

export const Button: React.FC<CustomButtonProps> = ({ 
  variant = 'default', 
  size = 'default', 
  iconName,
  iconPosition,
  fullWidth,
  children,
  className,
  ...props 
}) => {
  // Map custom variants to Ant Design types
  const getAntType = () => {
    switch (variant) {
      case 'primary': return 'primary';
      case 'danger':
      case 'destructive': return 'primary'; // Will use danger styling via className
      case 'outline':
      case 'outlined': return 'default';
      case 'ghost': return 'text';
      case 'link': return 'link';
      default: return 'default';
    }
  };

  // Map custom sizes to Ant Design sizes
  const getAntSize = () => {
    switch (size) {
      case 'xs':
      case 'sm': return 'small';
      case 'lg':
      case 'xl': return 'large';
      default: return 'middle';
    }
  };

  const antType = getAntType();
  const antSize = getAntSize();
  
  const buttonClassName = `${className || ''} ${fullWidth ? 'w-full' : ''}`.trim();

  return (
    <AntButton
      type={antType}
      size={antSize}
      className={buttonClassName}
      {...props}
    >
      {children}
    </AntButton>
  );
};

export {
  Input,
  Select,
  Checkbox,
  Card,
  Layout,
  Menu,
  Breadcrumb,
  Space,
  Typography,
  Row,
  Col,
  Divider,
  Tag,
  Badge,
  Avatar,
  Progress,
  Spin,
  Modal,
  Drawer,
  Table,
  Pagination,
  Switch,
  Slider,
  DatePicker,
  TimePicker,
  Upload,
  Form,
  Alert,
  message,
  notification,
  Popover,
  Tooltip,
  Dropdown,
} from 'antd';

export type { ButtonProps, InputProps, SelectProps, CheckboxProps } from 'antd';

// Icons
export {
  PlayCircleOutlined,
  PauseCircleOutlined,
  StopOutlined,
  SettingOutlined,
  UserOutlined,
  EyeOutlined,
  SearchOutlined,
  FilterOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  UploadOutlined,
  ShareAltOutlined,
  FullscreenOutlined,
  MenuOutlined,
  CloseOutlined,
  CheckOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  HomeOutlined,
  DashboardOutlined,
  VideoCameraOutlined,
  MonitorOutlined,
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined,
} from '@ant-design/icons';
